import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Toaster } from '@matillion/component-library'

import useLegacyMerge from 'api/hooks/useLegacyMerge/useLegacyMerge'

import { type MergeActionArgs, type MergeActionResult } from './types'
import useProblemDetails from 'hooks/useProblemDetails'
import { type ProblemDetailRegistry } from 'hooks/useProblemDetails/types'
import { Problem } from 'hooks/useProblemDetails/Problem'
import useMerge from 'api/hooks/useMerge'
import useMergeFeatureFlag from 'modules/Merge/hooks/useMergeFeatureFlag'
import useEventContext from 'provider/EventProvider/useEventContext'

const commonFailureTitleKey = 'merge.problem.default.title'

const problemDetailsRegistry: ProblemDetailRegistry = [
  {
    type: Problem.WTS_BRANCH_NOT_FOUND,
    translation: {
      titleKey: commonFailureTitleKey,
      messageKey: 'merge.problem.branch-not-found'
    }
  },
  {
    type: Problem.GIT_MANUAL_MERGE_REQUIRED,
    translation: {
      titleKey: commonFailureTitleKey,
      messageKey: 'merge.problem.manual-merge-required'
    }
  }
]

const isNotEmpty = (value?: string): value is string =>
  value !== undefined && value.trim() !== ''

const useMergeAction = ({
  mergeMessage,
  targetBranch,
  sourceBranch,
  targetCommitId,
  sourceCommitId,
  conflictSelections,
  onSuccess,
  onFailure
}: MergeActionArgs): MergeActionResult => {
  const { makeToast } = Toaster.useToaster()
  const { isMergingFromBranch, keyDiscriminator } = useMergeFeatureFlag()
  const { mutateAsync: legacyMerge, isLoading: isLegacyMergeLoading } =
    useLegacyMerge()
  const { refreshWorkingTreeStatus } = useEventContext()
  const { mutateAsync: wtsMerge, isLoading: isWTSMergeLoading } = useMerge()
  const { t } = useTranslation('translation', { keyPrefix: 'merge.toast' })
  const { resolveProblemDetails } = useProblemDetails(problemDetailsRegistry, [
    {
      name: 'targetBranch',
      value: targetBranch ?? ''
    }
  ])

  const isValid =
    isNotEmpty(mergeMessage) &&
    isNotEmpty(targetBranch) &&
    isNotEmpty(sourceBranch) &&
    isNotEmpty(targetCommitId) &&
    isNotEmpty(sourceCommitId)

  const isLoading = useMemo(() => {
    return isLegacyMergeLoading || isWTSMergeLoading
  }, [isLegacyMergeLoading, isWTSMergeLoading])

  const merge = useCallback(async () => {
    if (isLoading) return
    if (!isValid) return

    try {
      let commitId: string | undefined

      if (isMergingFromBranch) {
        await wtsMerge({
          remoteCommitId: targetCommitId,
          conflictSelections,
          sourceBranch,
          commitMessage: mergeMessage
        })

        await refreshWorkingTreeStatus?.({
          refreshFileSummaries: true,
          refreshPipelines: true
        })
      } else {
        const response = await legacyMerge({
          mergeMessage,
          targetBranch,
          sourceCommitId,
          targetCommitId,
          conflictSelections
        })
        commitId = response.commitId
      }

      makeToast({
        type: 'success',
        title: t(`success.${keyDiscriminator}.title`, {
          branch: isMergingFromBranch ? sourceBranch : targetBranch
        }),
        message: t(`success.${keyDiscriminator}.message`, {
          branch: targetBranch
        })
      })

      await onSuccess(commitId)
    } catch (error) {
      resolveProblemDetails(error)
      onFailure()
    }
  }, [
    isLoading,
    isValid,
    isMergingFromBranch,
    makeToast,
    t,
    keyDiscriminator,
    sourceBranch,
    targetBranch,
    onSuccess,
    wtsMerge,
    targetCommitId,
    conflictSelections,
    legacyMerge,
    mergeMessage,
    sourceCommitId,
    refreshWorkingTreeStatus,
    resolveProblemDetails,
    onFailure
  ])

  return { isLoading, merge, isValid }
}

export default useMergeAction
